import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit, Trash2, Power, PowerOff } from 'lucide-react';
import { userService } from '../../services/userService';
import { profileService } from '../../services/profileService';
import { SystemUser, Profile } from '../../types';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loading from '../../components/common/Loading';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import UserFormModal from './UserFormModal';

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<SystemUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getAll(currentPage, 15, search);
      setUsers(response.data);
      setTotalPages(response.last_page);
    } catch (err: any) {
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [currentPage, search]);

  const loadProfiles = async () => {
    try {
      const data = await profileService.getAll();
      setProfiles(data);
    } catch (err: any) {
      console.error('Erro ao carregar perfis:', err);
    }
  };

  useEffect(() => {
    loadUsers();
    loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadUsers]);

  const handleAdd = () => {
    setEditingUser(null);
    setIsFormOpen(true);
  };

  const handleEdit = (user: SystemUser) => {
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingUser) return;

    try {
      await userService.delete(deletingUser.id);
      setSuccess('Usuário excluído com sucesso!');
      setDeletingUser(null);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir usuário');
      setDeletingUser(null);
    }
  };

  const handleToggleActive = async (user: SystemUser) => {
    try {
      await userService.toggleActive(user.id);
      setSuccess(
        `Usuário ${user.active ? 'desativado' : 'ativado'} com sucesso!`
      );
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar usuário');
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingUser(null);
    loadUsers();
    setSuccess(
      editingUser ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!'
    );
  };

  const getProfileName = (profileId: number): string => {
    const profile = profiles.find((p) => p.id === profileId);
    return profile?.name || profile?.nome || 'N/A';
  };

  if (loading && users.length === 0) {
    return (
      <div className="p-8 flex justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Alerts */}
      {error && (
        <Alert type="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" className="mb-4" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Usuários do Sistema</h2>
          <p className="text-gray-600 mt-1">
            Gerencie os usuários que têm acesso ao sistema
          </p>
        </div>
        <Button onClick={handleAdd} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Novo Usuário
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <Input
          type="text"
          placeholder="Buscar por nome ou email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          icon={<Search className="h-5 w-5" />}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuário
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Perfil
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {user.name || user.nome}
                      {user.is_master && (
                        <Badge variant="primary" className="ml-2">
                          Master
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900">
                    {getProfileName(user.profile_id)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge variant={user.active ? 'success' : 'danger'}>
                    {user.active ? 'Ativo' : 'Inativo'}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`p-2 rounded-md transition-colors ${
                        user.active
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={user.active ? 'Desativar' : 'Ativar'}
                      disabled={user.is_master}
                    >
                      {user.active ? (
                        <PowerOff className="h-4 w-4" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(user)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {!user.is_master && (
                      <button
                        onClick={() => setDeletingUser(user)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Anterior
          </Button>
          <span className="px-4 py-2 text-sm text-gray-700">
            Página {currentPage} de {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Próxima
          </Button>
        </div>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <UserFormModal
          user={editingUser}
          profiles={profiles}
          onClose={() => {
            setIsFormOpen(false);
            setEditingUser(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <Modal
          isOpen={true}
          onClose={() => setDeletingUser(null)}
          title="Confirmar Exclusão"
        >
          <div className="p-6">
            <p className="text-gray-700 mb-6">
              Tem certeza que deseja excluir o usuário{' '}
              <strong>{deletingUser.name || deletingUser.nome}</strong>?
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeletingUser(null)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Excluir
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UsersPage;
